import {
  DocumentEditOutputListSchema,
  DocumentEditOutputSchema,
  DocumentEditorFinalizeResultSchema,
  DocumentEditorGateSchema,
  DocumentGitStatusSchema,
  DocumentListSchema,
  OfficeBootstrapResultSchema,
  DocumentMarkdownDraftSchema,
  DocumentProjectionSchema,
  DocumentRedactionDerivativeSchema,
  DocumentRedactionStageSchema,
  DocumentSaveCommandSchema,
  DocumentSearchCommandSchema,
  DocumentSearchResultSchema,
  DocumentUploadMetadataSchema,
  DocumentUploadResultSchema,
  DocumentVersionListSchema,
  ErrorEnvelopeSchema,
  UuidSchema,
  type DocumentEditOutput,
  type DocumentEditOutputList,
  type DocumentEditorFinalizeResult,
  type DocumentEditorGate,
  type DocumentGitStatus,
  type DocumentList,
  type OfficeBootstrapResult,
  type DocumentMarkdownDraft,
  type DocumentProjection,
  type DocumentRedactionStage,
  type DocumentRedactionDerivative,
  type DocumentSaveCommand,
  type DocumentSearchResult,
  type DocumentUploadMetadata,
  type DocumentUploadResult,
  type DocumentVersionList,
} from "@mind/contracts/browser";
import { z } from "zod";
import { ApiContractError, ApiError, apiRequest, type ApiRequestOptions } from "../../api/client.js";

const DocumentRedactionDerivativeListSchema = z.array(DocumentRedactionDerivativeSchema).max(2_000);
const DraftSaveInputSchema = z.object({
  baseVersionId: UuidSchema,
  savedStateVector: z.string().regex(/^[A-Za-z0-9_-]*$/).max(100_000),
  bytesBase64: z.string().max(33_554_432),
}).strict();
type PromiseResolvers<Output> = {
  readonly promise: Promise<Output>;
  readonly resolve: (value: Output | PromiseLike<Output>) => void;
  readonly reject: (reason?: unknown) => void;
};

function createPromiseResolvers<Output>(): PromiseResolvers<Output> {
  const native = (Promise as PromiseConstructor & {
    readonly withResolvers?: <Value>() => PromiseResolvers<Value>;
  }).withResolvers;
  if (native !== undefined) return native.call(Promise) as unknown as PromiseResolvers<Output>;
  let resolvePromise!: PromiseResolvers<Output>["resolve"];
  let rejectPromise!: PromiseResolvers<Output>["reject"];
  const promise = new Promise<Output>((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });
  return { promise, resolve: resolvePromise, reject: rejectPromise };
}

function withOptionalSignal(options: ApiRequestOptions, signal?: AbortSignal): ApiRequestOptions {
  return signal === undefined ? options : { ...options, signal };
}

function pathWithQuery(path: string, entries: Readonly<Record<string, string | number | boolean | null | undefined>>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(entries)) {
    if (value !== undefined && value !== null && value !== "") query.set(key, String(value));
  }
  const encoded = query.toString();
  return encoded.length === 0 ? path : `${path}?${encoded}`;
}

async function parseBinaryError(response: Response): Promise<never> {
  const text = await response.text();
  let payload: unknown;
  try {
    payload = JSON.parse(text);
  } catch (error) {
    throw new ApiContractError(response.status, "API returned malformed error envelope", { cause: error });
  }
  const envelope = ErrorEnvelopeSchema.safeParse(payload);
  if (!envelope.success) {
    throw new ApiContractError(response.status, "API returned a malformed error envelope", { cause: envelope.error });
  }
  throw new ApiError(response.status, envelope.data.error);
}

export type DocumentDownload = {
  readonly blob: Blob;
  readonly filename: string | null;
};

export type DocumentUploadProgress = {
  readonly loaded: number;
  readonly total: number;
};

export type DocumentUploadInput = {
  readonly workspaceId: string;
  readonly file: File;
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: DocumentUploadProgress) => void;
};

function bytesToSha256Hex(bytes: ArrayBuffer): Promise<string> {
  return crypto.subtle.digest("SHA-256", bytes).then((digest) =>
    Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join(""),
  );
}

function parseDownloadFilename(response: Response): string | null {
  const disposition = response.headers.get("content-disposition");
  if (disposition === null) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded !== undefined) {
    try { return decodeURIComponent(encoded); } catch { return encoded; }
  }
  return /filename="([^"]+)"/i.exec(disposition)?.[1] ?? null;
}

async function download(path: string, signal?: AbortSignal): Promise<DocumentDownload> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...(signal === undefined ? {} : { signal }),
    headers: { accept: "application/octet-stream" },
  });
  if (!response.ok) return parseBinaryError(response);
  return { blob: await response.blob(), filename: parseDownloadFilename(response) };
}

function uploadFile(input: DocumentUploadInput, metadata: DocumentUploadMetadata): Promise<DocumentUploadResult> {
  const { promise, resolve, reject } = createPromiseResolvers<DocumentUploadResult>();
  const xhr = new XMLHttpRequest();
  let settled = false;
  const finish = (callback: () => void): boolean => {
    if (settled) return false;
    settled = true;
    input.signal?.removeEventListener("abort", abort);
    callback();
    return true;
  };
  const abort = (): void => {
    if (!finish(() => reject(new DOMException("The upload was aborted", "AbortError")))) return;
    xhr.abort();
  };
  xhr.upload.addEventListener("progress", (event) => {
    if (event.lengthComputable) input.onProgress?.({ loaded: event.loaded, total: event.total });
  });
  xhr.addEventListener("error", () => finish(() => reject(new Error("Document upload failed"))));
  xhr.addEventListener("abort", () => finish(() => reject(new DOMException("The upload was aborted", "AbortError"))));
  xhr.addEventListener("load", () => {
    finish(() => {
      let payload: unknown;
      try { payload = JSON.parse(xhr.responseText); } catch (error) {
        reject(new ApiContractError(xhr.status, "API returned malformed JSON", { cause: error }));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        const envelope = ErrorEnvelopeSchema.safeParse(payload);
        if (!envelope.success) {
          reject(new ApiContractError(xhr.status, "API returned a malformed error envelope", { cause: envelope.error }));
        } else {
          reject(new ApiError(xhr.status, envelope.data.error));
        }
        return;
      }
      const parsed = DocumentUploadResultSchema.safeParse(payload);
      if (!parsed.success) {
        reject(new ApiContractError(xhr.status, "API response violated its schema", { cause: parsed.error }));
        return;
      }
      resolve(parsed.data);
    });
  });
  xhr.open("POST", "/api/documents");
  xhr.withCredentials = true;
  xhr.setRequestHeader("Accept", "application/json");
  input.signal?.addEventListener("abort", abort, { once: true });
  const form = new FormData();
  form.append("metadata", JSON.stringify(metadata));
  form.append("file", input.file, input.file.name);
  if (input.signal?.aborted === true) abort();
  else xhr.send(form);
  return promise;
}

export const documentApi = {
  list(workspaceId: string, signal?: AbortSignal): Promise<DocumentList> {
    return apiRequest(DocumentListSchema, pathWithQuery("/api/documents", { workspaceId: UuidSchema.parse(workspaceId) }), withOptionalSignal({}, signal));
  },
  search(workspaceId: string, query: string, signal?: AbortSignal): Promise<DocumentSearchResult> {
    const body = DocumentSearchCommandSchema.parse({ workspaceId, query, cursor: null, limit: 100 });
    return apiRequest(DocumentSearchResultSchema, "/api/documents/search", withOptionalSignal({ method: "POST", body }, signal));
  },
  get(documentId: string, signal?: AbortSignal): Promise<DocumentProjection> {
    return apiRequest(DocumentProjectionSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}`, withOptionalSignal({}, signal));
  },
  versions(documentId: string, signal?: AbortSignal): Promise<DocumentVersionList> {
    return apiRequest(DocumentVersionListSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/versions`, withOptionalSignal({}, signal));
  },
  getDraft(documentId: string, signal?: AbortSignal): Promise<DocumentMarkdownDraft> {
    return apiRequest(DocumentMarkdownDraftSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/draft`, withOptionalSignal({}, signal));
  },
  saveDraft(documentId: string, input: Pick<DocumentMarkdownDraft, "baseVersionId" | "savedStateVector" | "bytesBase64">): Promise<DocumentMarkdownDraft> {
    const body = DraftSaveInputSchema.parse(input);
    return apiRequest(DocumentMarkdownDraftSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/draft`, { method: "PUT", body });
  },
  saveMarkdown(input: DocumentSaveCommand): Promise<DocumentProjection> {
    return apiRequest(DocumentProjectionSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(input.documentId))}/save`, { method: "POST", body: DocumentSaveCommandSchema.parse(input) });
  },
  openEditor(documentId: string, mode: "view" | "edit", signal?: AbortSignal): Promise<DocumentEditorGate> {
    return apiRequest(
      DocumentEditorGateSchema,
      pathWithQuery(`/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/editor`, { mode }),
      withOptionalSignal({ method: "POST", body: {} }, signal),
    );
  },
  bootstrapOffice(officeOrigin: string, token: string, signal?: AbortSignal): Promise<OfficeBootstrapResult> {
    return apiRequest(OfficeBootstrapResultSchema, `${officeOrigin}/mind/bootstrap`, { method: "POST", credentials: "include", body: { token }, ...(signal === undefined ? {} : { signal }) });
  },
  closeEditor(documentId: string, signal?: AbortSignal): Promise<DocumentProjection> {
    return apiRequest(DocumentProjectionSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/editor`, withOptionalSignal({ method: "POST", body: {} }, signal));
  },
  finalizeEditor(documentId: string, signal?: AbortSignal): Promise<DocumentEditorFinalizeResult> {
    return apiRequest(DocumentEditorFinalizeResultSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/editor/finalize`, withOptionalSignal({ method: "POST", body: {} }, signal));
  },
  gitStatus(documentId: string, signal?: AbortSignal): Promise<DocumentGitStatus> {
    return apiRequest(DocumentGitStatusSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/git-status`, withOptionalSignal({}, signal));
  },
  editOutputs(documentId: string, signal?: AbortSignal): Promise<DocumentEditOutputList> {
    return apiRequest(DocumentEditOutputListSchema, pathWithQuery(`/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/edit-outputs`, { limit: 100 }), withOptionalSignal({}, signal));
  },
  stageEditOutput(documentId: string, outputId: string): Promise<DocumentEditOutput> {
    return apiRequest(DocumentEditOutputSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/edit-outputs/${encodeURIComponent(UuidSchema.parse(outputId))}/stage`, { method: "POST", body: {} });
  },
  discardEditOutput(documentId: string, outputId: string): Promise<DocumentEditOutput> {
    return apiRequest(DocumentEditOutputSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/edit-outputs/${encodeURIComponent(UuidSchema.parse(outputId))}/discard`, { method: "POST", body: {} });
  },
  redactionDerivatives(documentId: string, signal?: AbortSignal): Promise<readonly DocumentRedactionDerivative[]> {
    return apiRequest(DocumentRedactionDerivativeListSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/redaction-derivatives`, withOptionalSignal({}, signal));
  },
  stageRedactionDerivative(documentId: string, input: DocumentRedactionStage): Promise<DocumentRedactionDerivative> {
    return apiRequest(DocumentRedactionDerivativeSchema, `/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/redaction-derivatives`, { method: "POST", body: DocumentRedactionStageSchema.parse(input) });
  },
  downloadRedactionDerivative(documentId: string, derivativeId: string, signal?: AbortSignal): Promise<DocumentDownload> {
    return download(`/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/redaction-derivatives/${encodeURIComponent(UuidSchema.parse(derivativeId))}/download`, signal);
  },
  downloadCurrent(documentId: string, signal?: AbortSignal): Promise<DocumentDownload> {
    return download(`/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/download`, signal);
  },
  downloadVersion(documentId: string, versionId: string, signal?: AbortSignal): Promise<DocumentDownload> {
    return download(`/api/documents/${encodeURIComponent(UuidSchema.parse(documentId))}/versions/${encodeURIComponent(UuidSchema.parse(versionId))}/download`, signal);
  },
  upload(input: DocumentUploadInput): Promise<DocumentUploadResult> {
    return input.file.arrayBuffer().then(async (bytes) => {
      const metadata = DocumentUploadMetadataSchema.parse({
        commandId: crypto.randomUUID(),
        documentId: crypto.randomUUID(),
        workspaceId: UuidSchema.parse(input.workspaceId),
        displayName: input.file.name,
        mediaType: input.file.type || "application/octet-stream",
        byteLength: String(input.file.size),
        sha256: await bytesToSha256Hex(bytes),
      });
      return uploadFile(input, metadata);
    });
  },
};


export function decodeBase64Utf8(value: string): string {
  const binary = atob(value);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function encodeUtf8Base64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function createMarkdownSaveCommand(
  document: DocumentProjection,
  draft: Pick<DocumentMarkdownDraft, "baseVersionId" | "savedStateVector">,
  text: string,
): DocumentSaveCommand {
  return DocumentSaveCommandSchema.parse({
    commandId: crypto.randomUUID(),
    documentId: document.id,
    baseVersionId: draft.baseVersionId,
    expectedDocumentRevision: document.revision,
    savedStateVector: draft.savedStateVector,
    bytesBase64: encodeUtf8Base64(text),
  });
}